import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import { Send as SendIcon, Edit as EditIcon, Person as PersonIcon } from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';
import { handleApiError } from '../../utils/apiErrorHandler';
import { toast } from '../../utils/toast';

interface TemplateData {
  recipientEmail: string;
  recipientName: string;
  bccEmail: string;
  subject: string;
  templateText: string;
  waitlistAthletes: { Elite: string[]; Basis: string[] };
  competitionName: string;
  clubName: string;
}

interface RegistrationReminderEmailDialogProps {
  open: boolean;
  onClose: () => void;
  competitionId: string;
  clubId: string;
  clubName: string;
  competitionName: string;
  onSent: () => void;
}

const RegistrationReminderEmailDialog: React.FC<RegistrationReminderEmailDialogProps> = ({
  open,
  onClose,
  competitionId,
  clubId,
  clubName,
  competitionName,
  onSent
}) => {
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [emailBody, setEmailBody] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && clubId) {
      fetchTemplate();
    }
  }, [open, clubId]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/api/competition-registrations/registration-reminder-template', {
        params: { competitionId, clubId }
      });
      setTemplateData(response.data);
      setEmailBody(response.data.templateText);
      setEmailSubject(response.data.subject);
    } catch (err) {
      handleApiError(err, 'Fehler beim Laden der E-Mail-Vorlage.');
      setError('Die E-Mail-Vorlage konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!emailBody.trim()) return;
    try {
      setSending(true);
      await axiosInstance.post('/api/competition-registrations/send-registration-reminder', {
        competitionId,
        clubId,
        emailBody,
        emailSubject
      });
      toast.success(`Erinnerungsmail an ${clubName} wurde erfolgreich versendet.`);
      onSent();
      handleClose();
    } catch (err) {
      handleApiError(err, 'Fehler beim Versenden der Erinnerungsmail');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setEditMode(false);
    setError(null);
    setTemplateData(null);
    setEmailBody('');
    setEmailSubject('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="primary" />
          <Typography variant="h6">Meldungserinnerung senden</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : templateData ? (
          <Box>
            {/* Info-Box */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Wettkampf:</Typography>
                  <Typography variant="body2">{templateData.competitionName}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Verein:</Typography>
                  <Typography variant="body2">{templateData.clubName}</Typography>
                </Grid>
                {templateData.waitlistAthletes.Elite.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Warteliste Elite:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {templateData.waitlistAthletes.Elite.map((name, i) => (
                        <Chip key={i} icon={<PersonIcon />} label={name} size="small" variant="outlined" color="primary" />
                      ))}
                    </Box>
                  </Grid>
                )}
                {templateData.waitlistAthletes.Basis.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Warteliste Basis:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {templateData.waitlistAthletes.Basis.map((name, i) => (
                        <Chip key={i} icon={<PersonIcon />} label={name} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Empfänger */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">An (Meldeverantwortlicher):</Typography>
                <Typography variant="body2">{templateData.recipientEmail}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">BCC (Wettkampforganisator):</Typography>
                <Typography variant="body2">{templateData.bccEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Betreff:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{emailSubject}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* E-Mail-Text */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">E-Mail-Text:</Typography>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(!editMode)}
                  variant={editMode ? 'contained' : 'outlined'}
                >
                  {editMode ? 'Vorschau' : 'Bearbeiten'}
                </Button>
              </Box>

              {editMode ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={12}
                  maxRows={20}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  variant="outlined"
                  placeholder="E-Mail-Text eingeben..."
                />
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'grey.50',
                    minHeight: 250,
                    maxHeight: 400,
                    overflow: 'auto',
                    whiteSpace: 'pre-line',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem'
                  }}
                >
                  {emailBody}
                </Box>
              )}
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={sending}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="primary"
          disabled={loading || sending || !emailBody.trim()}
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {sending ? 'Wird gesendet...' : 'E-Mail senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationReminderEmailDialog;
